import type { PhaseState } from '@badger-sentinel/protocol';

/**
 * The window countdown (§8.2, shell.md §Time).
 *
 * Pure, and it takes **server-corrected** milliseconds rather than reading a
 * clock. That is not tidiness: `Date.now()` on a laptop 40 s fast closes the
 * orders window 40 s early, and the only defence that actually holds is making
 * the local clock unreachable from the function that renders the number.
 *
 * `PhaseState` distinguishes four reasons a countdown may not be counting and
 * shell.md requires all four to look different. The failure they exist to
 * prevent is a frozen `00:00` — which reads as a broken console, sends the
 * player to the GM, and costs the turn.
 */

export type CountdownKind =
  | 'RUNNING'
  | 'URGENT'
  | 'PAUSED'
  | 'LOBBY'
  | 'COMPLETE'
  | 'ABANDONED'
  /** RUNNING with no deadline — the resolution phase. The phase cell says which. */
  | 'NONE';

export interface CountdownView {
  readonly kind: CountdownKind;
  /** `mm:ss`, or an em dash where there is no number to show. */
  readonly clock: string;
  /**
   * The state word beside the clock. Null while a window is simply running,
   * because "RUNNING" beside a moving number is the console explaining itself.
   */
  readonly note: string | null;
  readonly remainingMs: number | null;
}

/** §13.2's distinct last-minute state. */
export const COUNTDOWN_URGENT_MS = 60_000;

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

const NO_CLOCK = '—';

export function countdownFor(phase: PhaseState | null, serverNowMs: number): CountdownView {
  if (phase === null) {
    return { kind: 'NONE', clock: NO_CLOCK, note: null, remainingMs: null };
  }

  switch (phase.status) {
    case 'LOBBY':
      return { kind: 'LOBBY', clock: NO_CLOCK, note: 'NOT STARTED', remainingMs: null };
    case 'COMPLETE':
      return { kind: 'COMPLETE', clock: NO_CLOCK, note: 'COMPLETE', remainingMs: null };
    case 'ABANDONED':
      return { kind: 'ABANDONED', clock: NO_CLOCK, note: 'ABANDONED', remainingMs: null };
    case 'PAUSED':
      // §4.10: a paused game carries what was left on the clock instead of a
      // deadline. Showing that number *and* the word is the difference between
      // "the GM stopped this" and "the timer died".
      return {
        kind: 'PAUSED',
        clock: phase.remainingMs === null ? NO_CLOCK : formatClock(phase.remainingMs),
        note: 'PAUSED',
        remainingMs: phase.remainingMs,
      };
    case 'RUNNING': {
      if (phase.deadlineAtMs === null) {
        return { kind: 'NONE', clock: NO_CLOCK, note: null, remainingMs: null };
      }
      const remainingMs = Math.max(0, phase.deadlineAtMs - serverNowMs);
      return {
        kind: remainingMs <= COUNTDOWN_URGENT_MS ? 'URGENT' : 'RUNNING',
        clock: formatClock(remainingMs),
        note: null,
        remainingMs,
      };
    }
  }
}
